import { Controller } from '@hotwired/stimulus'
import { loadStripe } from '@stripe/stripe-js'

import { commissionPercentageForPaymentMethod } from '../shared/wallet_commission_percentage'

export default class extends Controller {
  static targets = [
    'paymentElement',
    'message',
    'spinner',
    'submit',
    'amountInput',
    'creditCommissionPercentage',
    'achCommissionPercentage',
    'customFeesCalculation',
    'transactionFeeType',
    'transactionFlatFee',
    'merchantFeePortion',
    'transactionMaxCommission',
    'remainingCompanyFeeCap',
    'walletAmountLabel',
    'commissionAmountLabel',
    'commissionCheckbox',
    'matchingCheckbox',
    'minimumMatchingAmount',
    'minimumTxnAmount',
    'selectedMatchRate'
  ]
  static returnURL = `${window.location.origin}/wallets/wallets/complete`

  async initialize () {
    this.stripePublicKey = this.element.dataset.stripePublicKey
    this.stripe = await loadStripe(this.stripePublicKey)
    this.recurring = new URLSearchParams(window.location.search).get('recurring') === 'true'
    if (!this.stripe) {
      this.showMessage('Failed to load Stripe.')
      return
    }

    if (this.hasMatchingCheckboxTarget) {
      this.matchingCheckboxTarget.addEventListener('change', this.updateAmount.bind(this))
    }
    if (this.hasSelectedMatchRateTarget) {
      this.selectedMatchRateTarget.addEventListener('change', this.updateAmount.bind(this))
    }

    if (this.hasAmountInputTarget) {
      this.amountInputTarget.addEventListener('input', this.updateAmount.bind(this))
      this.amountInputTarget.addEventListener('keyup', this.updateAmount.bind(this))
      this.amountInputTarget.addEventListener('change', this.updateAmount.bind(this))
    }

    // Default until Payment Element fires `change` (card vs bank tab).
    this.selectedPaymentMethodType = 'card'

    this.updateAmount()
    this.createPaymentIntent('51.50', true)
  }

  async setupStripeElements (clientSecret) {
    this.elements = this.stripe.elements({ clientSecret })
    const paymentElement = this.elements.create('payment', {
      fields: { billing_details: { name: 'auto' } }
    })

    paymentElement.on('change', this.updatePayButtonText.bind(this))
    paymentElement.mount(this.paymentElementTarget)
    this.paymentElementTarget.classList.remove('hidden')
  }

  updatePayButtonText (event) {
    if (!event?.value) return
    const paymentMethod = event.value?.type
    if (paymentMethod === 'us_bank_account' || paymentMethod === 'card') {
      this.selectedPaymentMethodType = paymentMethod
    }
    if (paymentMethod === 'us_bank_account') {
      this.submitTarget.textContent = 'Pay with Bank Account'
    } else if (paymentMethod === 'card') {
      this.submitTarget.textContent = 'Pay with Credit Card'
    } else {
      this.submitTarget.textContent = 'Pay'
    }
    if (this.hasAmountInputTarget) {
      this.updateAmount()
    }
  }

  parseMoneyTarget (targetName) {
    const target = this[`${targetName}Target`]
    if (!target) return 0
    const parsed = parseFloat(target.value)
    return Number.isNaN(parsed) ? 0 : parsed
  }

  parseOptionalMoneyTarget (targetName) {
    const target = this[`${targetName}Target`]
    if (!target) return null
    const raw = `${target.value}`.trim()
    if (raw === '') return null
    const parsed = parseFloat(raw)
    return Number.isNaN(parsed) ? null : parsed
  }

  customFeesEnabled () {
    if (!this.hasCustomFeesCalculationTarget) return false
    return String(this.customFeesCalculationTarget.value) === 'true'
  }

  effectiveCommissionPercentage () {
    return commissionPercentageForPaymentMethod({
      paymentMethodType: this.selectedPaymentMethodType || 'card',
      creditCommissionPercentage: this.creditCommissionPercentageTarget.value,
      achCommissionPercentage: this.hasAchCommissionPercentageTarget
        ? this.achCommissionPercentageTarget.value
        : undefined
    })
  }

  applyFeeCaps (commissionAmount) {
    const capCandidates = []

    if (this.hasTransactionMaxCommissionTarget) {
      const maxCommission = this.parseMoneyTarget('transactionMaxCommission')
      if (maxCommission > 0) capCandidates.push(maxCommission)
    }

    if (this.hasRemainingCompanyFeeCapTarget) {
      const remainingCap = this.parseOptionalMoneyTarget('remainingCompanyFeeCap')
      if (remainingCap !== null && remainingCap >= 0) capCandidates.push(remainingCap)
    }

    if (!capCandidates.length) return commissionAmount
    return Math.min(commissionAmount, ...capCandidates)
  }

  merchantFeeRate () {
    if (!this.hasMerchantFeePortionTarget || this.merchantFeePortionTarget.value === '') return 0.02

    const portion = parseFloat(this.merchantFeePortionTarget.value)
    if (Number.isNaN(portion)) return 0.02

    return portion > 1 ? portion / 100 : portion
  }

  merchantFeeEligibleForMethod () {
    return ['card', 'us_bank_account', 'paypal', 'creditCard', 'ach', 'payPal'].includes(this.selectedPaymentMethodType)
  }

  calculateCommissionAmount (payingAmount) {
    if (!(this.hasCommissionCheckboxTarget && this.commissionCheckboxTarget.checked)) {
      return 0
    }

    const commissionPercentage = this.effectiveCommissionPercentage()
    let commissionAmount = (payingAmount * commissionPercentage) / 100

    if (this.customFeesEnabled()) {
      const feeType = this.hasTransactionFeeTypeTarget ? this.transactionFeeTypeTarget.value : 'variable'
      if (feeType === 'flat') {
        commissionAmount = this.parseMoneyTarget('transactionFlatFee')
      }

      commissionAmount = this.applyFeeCaps(commissionAmount)

      if (this.merchantFeeEligibleForMethod()) {
        commissionAmount += payingAmount * this.merchantFeeRate()
      }
    }

    return commissionAmount
  }

  toggleCommission () {
    this.updateAmount()
  }

  bankersRound (value, decimals = 2) {
    const d = Math.pow(10, decimals)
    const n = value * d
    const f = Math.floor(n)
    const r = n - f
    if (r > 0.5) return (f + 1) / d
    if (r < 0.5) return f / d
    return ((f % 2 === 0 ? f : f + 1) / d)
  }

  effectiveMatchRate () {
    if (this.hasSelectedMatchRateTarget) {
      const raw = this.selectedMatchRateTarget.value
      const v = typeof raw === 'string' && raw.indexOf('%') >= 0 ? parseFloat(raw) / 100 : parseFloat(raw)
      if (!Number.isNaN(v)) return v
    }
    const hidden = document.getElementById('wallet_default_match_rate')
    const hv = hidden ? parseFloat(hidden.value) : NaN
    return Number.isNaN(hv) ? 1.0 : hv
  }

  currencySymbol () {
    const code = 'USD'
    try {
      const nf = new Intl.NumberFormat(undefined, { style: 'currency', currency: code, currencyDisplay: 'symbol', maximumFractionDigits: 0 })
      const parts = nf.formatToParts(1)
      const c = parts.find(p => p.type === 'currency')
      if (c && c.value) return c.value
    } catch (_) {}
    return '$'
  }

  companyName () {
    return (
      this.element?.dataset?.companyName ||
      (document.querySelector('.matching-company')?.textContent || '').trim()
    )
  }

  placeWarningAboveRateLabel (node) {
    const rateLabel = this.element.querySelector('label[for="selected_match_rate"]')
    if (rateLabel && node.nextElementSibling !== rateLabel) {
      rateLabel.parentNode.insertBefore(node, rateLabel)
    }
  }

  updateAmount () {
    const payingAmount = parseFloat(this.amountInputTarget.value)
    const minimumMatchingAmount = parseFloat(this.minimumMatchingAmountTarget.value)
    const minimumTxnAmount = parseFloat(this.minimumTxnAmountTarget.value)

    if (isNaN(payingAmount)) {
      this.showMessage('Please enter a valid amount.')
      this.resetAmountToDefault()
      return
    }

    if (payingAmount < 0.5) {
      this.showMessage('The amount should not be less than $0.5!')
      this.resetAmountToDefault()
      return
    }

    if (payingAmount < minimumTxnAmount) {
      this.submitTarget.disabled = true
      const minimumInfo = this.element.querySelector('.minimum-amount-info')
      if (minimumInfo) {
        minimumInfo.classList.remove('hidden')
      }
    } else {
      this.submitTarget.disabled = false
      const minimumInfo = this.element.querySelector('.minimum-amount-info')
      if (minimumInfo) {
        minimumInfo.classList.add('hidden')
      }
    }

    const processingFeeDisplay = (payingAmount * this.effectiveCommissionPercentage()) / 100
    let totalAmount = payingAmount

    const commissionAmount = this.calculateCommissionAmount(payingAmount)
    totalAmount += commissionAmount

    if (this.hasCommissionAmountLabelTarget) {
      const labelAmount = this.customFeesEnabled() ? commissionAmount : processingFeeDisplay
      this.commissionAmountLabelTarget.textContent = `$${labelAmount.toFixed(2)}`
    }

    if (this.hasWalletAmountLabelTarget) {
      this.walletAmountLabelTarget.textContent = `$${totalAmount.toFixed(2)}`
    }

    if (this.hasMatchingCheckboxTarget) {
      const el = this.element.querySelector('.matching-info')
      if (el) {
        const rate = this.effectiveMatchRate()
        const matchPayout = this.bankersRound(payingAmount * rate, 2)
        const shouldShow = this.matchingCheckboxTarget.checked && (matchPayout < minimumMatchingAmount)

        if (shouldShow) {
          const symbol = this.currencySymbol()
          const minStr = `${symbol}${minimumMatchingAmount.toFixed(2)}`
          const company = this.companyName()
          const suffix = company ? ` from ${company}` : ''
          el.textContent = `Match amount must be ${minStr} or more to be eligible for a match${suffix}.`
          this.placeWarningAboveRateLabel(el)
          el.classList.remove('hidden')
        } else {
          el.textContent = ''
          el.classList.add('hidden')
        }
      }
    }
  }

  calculateTotalAmount () {
    const payingAmount = parseFloat(this.amountInputTarget.value) || 0
    const commissionAmount = this.calculateCommissionAmount(payingAmount)
    return payingAmount + commissionAmount
  }

  async createPaymentIntent (amount, initializeElements = false, formState = null) {
    try {
      const clientSecret = await this.fetchPaymentIntentClientSecret(amount, formState)
      if (initializeElements && !this.elements) {
        await this.setupStripeElements(clientSecret)
      }
    } catch (error) {
      this.showMessage('An error occurred while processing your payment. Please try again later.')
      console.error('Payment Intent Error:', error)
    }
  }

  getFormState () {
    const userAllowsMatching = this.hasMatchingCheckboxTarget
      ? this.matchingCheckboxTarget.checked
      : false
    const matchRate = this.hasSelectedMatchRateTarget
      ? this.selectedMatchRateTarget.value
      : ''
    const userPayCommission = this.hasCommissionCheckboxTarget
      ? this.commissionCheckboxTarget.checked
      : false
    return { userAllowsMatching, matchRate, userPayCommission }
  }

  resetAmountToDefault () {
    const amountInput = this.amountInputTarget
    if (amountInput) {
      amountInput.value = 0.5
      this.updateAmount()
    }
  }

  async fetchPaymentIntentClientSecret (amount, formState = null) {
    const body = {
      items: [{ id: 'custom_item', amount: Math.round(amount * 100) }],
      paymentMethodTypes: ['card', 'us_bank_account'],
      payment_intent_id: this.paymentIntentId || null
    }
    if (formState) {
      body.user_allows_matching = formState.userAllowsMatching
      body.match_rate = formState.matchRate != null && formState.matchRate !== '' ? formState.matchRate : ''
      body.user_pays_commission = formState.userPayCommission
    }
    const response = await fetch('/wallets/wallets/create_payment_intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    const { clientSecret, paymentIntentId } = await response.json()
    this.paymentIntentId = paymentIntentId
    return clientSecret
  }

  async submit (event) {
    event.preventDefault()
    this.setLoading(true)
    try {
      const totalAmount = this.calculateTotalAmount()
      await this.createPaymentIntent(totalAmount.toFixed(2), false, this.getFormState())

      const { error } = await this.confirmPayment()
      if (error) {
        this.showMessage(error.message)
      } else {
        const returnUrl = `${this.constructor.returnURL}?recurring=${this.recurring}`
        window.location.href = returnUrl
      }
    } catch (error) {
      this.showMessage('Payment processing failed.')
    }
    this.setLoading(false)
  }

  async confirmPayment () {
    const userPayCommission = this.hasCommissionCheckboxTarget
      ? this.commissionCheckboxTarget.checked
      : false

    const amount = this.hasAmountInputTarget
      ? this.amountInputTarget.value
      : 0

    const userAllowsMatching = this.hasMatchingCheckboxTarget
      ? this.matchingCheckboxTarget.checked
      : false

    const matchRate = this.hasSelectedMatchRateTarget
      ? this.selectedMatchRateTarget.value
      : 0

    const returnUrl = `${this.constructor.returnURL}?recurring=${this.recurring}&user_pays_commission=${userPayCommission}&amount=${amount}&user_allows_matching=${userAllowsMatching}&match_rate=${matchRate}`

    return this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: { return_url: returnUrl }
    })
  }

  showMessage (message) {
    const flashContainer = document.getElementById('processing-err')
    if (flashContainer) {
      flashContainer.innerHTML = `
        <div>
          ${message}
        </div>`
      setTimeout(() => {
        flashContainer.innerHTML = ''
      }, 5000)
    } else {
      console.error('Flash container not found')
    }

    this.messageTarget.textContent = message
    this.messageTarget.classList.remove('hidden')
    setTimeout(() => {
      this.messageTarget.classList.add('hidden')
      this.messageTarget.textContent = ''
    }, 4000)
  }

  setLoading (isLoading) {
    this.submitTarget.disabled = isLoading
    if (isLoading === true) {
      this.submitTarget.classList.add('hidden')
    } else {
      this.submitTarget.classList.remove('hidden')
    }
    this.spinnerTarget.classList.toggle('hidden', !isLoading)
  }

  getAmount () {
    return parseFloat(this.amountInputTarget.value) || 0
  }

  isValidAmount (amount) {
    return !isNaN(amount) && amount > 0
  }
}
